/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { utils, Point, Rectangle } from 'pixi.js-legacy';
import { ComponentManager } from '@appCanvasCommon/component-system/ComponentManager';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { Swimlane, SwimlaneRenderTypes } from 'src/app/app-data/models/Swimlane';

import { EventBinder } from '../interaction/interaction-utils';
import { SWIMLANE_NO_EDIT_RIGHTS } from '@touchplan/strings';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';

import { getTicketsInRect } from 'utils/ticket-utils';
import { Ticket } from '@touchplan/common';
import { SWIMLANE_GRAPHICS, SWIMLANE_LABEL_SETTINGS, SWIMLANE_LABEL_STYLE } from './SwimlaneConst';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { CursorType } from '@appCanvasCommon/events/cursor-type.enum';
import { ISwimlaneScreenPosition } from '@appPlanApplication/swimlanes/FancySwimlanes';

export class SwimlaneLabel extends HighResolutionText {
	public components: ComponentManager;
	public background: FancySprite;
	public data: Swimlane;

	private mode: string;
	public stickyY: number;
	private _lastText: string;
	private _localPosition: Point = new Point(0, 0);
	private binder: EventBinder;
	private backgroundBinder: EventBinder;

	constructor(swimlaneModel: Swimlane, private canvasAngularLand: CanvasAngularLand) {
		super('', new FancyTextStyle(SWIMLANE_LABEL_STYLE));
		this.data = swimlaneModel;

		this.components = new ComponentManager(this);

		// Create label background
		this.background = new FancySprite(utils.TextureCache[SWIMLANE_GRAPHICS.SWIMLANE_TEXT_BACKGROUND]);
		this.background.eventMode = EventMode.Static;
		this.background.cursor = CursorType.Pointer;
		this.background.visible = true;
		this.background.alpha = SWIMLANE_LABEL_SETTINGS.BACKGROUND_ALPHA;

		this.binder = new EventBinder(this);
		this.backgroundBinder = new EventBinder(this.background);

		this.binder.add('hammer-singletap', this.onSingleTap);
		this.binder.add('hammer-doubletap', this.onDoubleTap);

		this.backgroundBinder.add('hammer-singletap', this.onSingleTap);
		this.backgroundBinder.add('hammer-doubletap', this.onDoubleTap);

		// this.on('hammer-singletap', this.onSingleTap);
	}

	public update(swimlaneModel: Swimlane): void {
		this.mode =
			swimlaneModel.currentRenderType === SwimlaneRenderTypes.edit ? SwimlaneRenderTypes.edit : 'view';

		this.setText(swimlaneModel[this.mode].label);
		const screenPosition: ISwimlaneScreenPosition = {
			screenTop: swimlaneModel.view.screenTop as number,
			screenBottom: swimlaneModel.view.screenBottom as number,
		};
		this._calculateBackground(screenPosition);
	}

	public onSingleTap = () => {
		if (
			this.canvasAngularLand.planAccessCheck.isAdminSync &&
			!this.canvasAngularLand.planAccessCheck.hasRestrictedAccessSync
		) {
			const newView: string =
				this.data.currentRenderType === SwimlaneRenderTypes.edit
					? SwimlaneRenderTypes.database
					: SwimlaneRenderTypes.edit;
			window.blehTimestamp = performance.now();
			this.canvasAngularLand.swimlanes.changeView(this.data, newView);
		} else {
			window.Logging.warning(SWIMLANE_NO_EDIT_RIGHTS);
		}
	};
	public onDoubleTap = () => {
		const s = this.data.view;
		const rect = new Rectangle(-Infinity, s.top, Infinity, s.bottom - s.top);
		const rawTickets = getTicketsInRect(this.canvasAngularLand.tickets.syncList, rect);

		let tickets: typeof rawTickets = [];

		const authedProjectMember = this.canvasAngularLand.authedProjectMemberSync;

		if (this.canvasAngularLand.planAccessCheck.isAdminSync) {
			tickets = rawTickets;
		} else {
			rawTickets.forEach((t) => {
				if (this.canvasAngularLand.planAccessCheck.hasAccessToTicketSync(t)) {
					tickets.push(t);
				}
			});
		}

		let allSame = true;
		let selected;
		let idx = 0;
		tickets.forEach((s) => {
			if (idx === 0) {
				selected = s.selected;
			} else if (s.selected !== selected) {
				allSame = false;
			}
			idx++;
		});
		if (allSame && selected) {
			// need a map<$id, Ticket> for clearSelection action
			const ticketMap: Map<string, Ticket> = new Map<string, Ticket>();
			for (const ticket of tickets) {
				ticketMap.set(ticket.$id, ticket);
			}
			this.canvasAngularLand.actions.clearSelection(ticketMap);
		} else {
			this.canvasAngularLand.actions.selectTicket(tickets);
		}
	};

	public setText(text: string = ''): SwimlaneLabel {
		if (text === this._lastText) {
			return;
		}
		this._lastText = text;
		// Text is too long. Trim it!
		if (text.length >= SWIMLANE_LABEL_SETTINGS.LABEL_MAX_LENGTH) {
			text = text.slice(0, SWIMLANE_LABEL_SETTINGS.LABEL_MAX_LENGTH) + '...';
		}
		this.text = '' + text; // Doing the "" + here to handle swimlanes that is a number
		return this;
	}

	public getData(): any {
		return this.data;
	}

	public override destroy(options?): void {
		super.destroy(options);

		this.binder.destroy();
		this.backgroundBinder.destroy();

		this.background.destroy();
		this.background = null;

		this.components.destroy(options);
		this.components = null;
	}

	public updatePosition(screenPosition: ISwimlaneScreenPosition): void {
		this.setText(this.data.view.label);
		this._calculateBackground(screenPosition);
	}

	private _calculateBackground(screenPosition: ISwimlaneScreenPosition): void {
		// make it sticky
		this.stickyY =
			screenPosition.screenTop <= SWIMLANE_LABEL_SETTINGS.LABEL_STICKINESS_THRESHOLD &&
			screenPosition.screenBottom >=
				SWIMLANE_LABEL_SETTINGS.LABEL_STICKINESS_THRESHOLD + this.background.height
				? SWIMLANE_LABEL_SETTINGS.LABEL_STICKINESS_THRESHOLD
				: screenPosition.screenTop + SWIMLANE_LABEL_SETTINGS.LABEL_TOP_PADDING;

		this.background.x = 86;
		this.background.y = this.stickyY;
		this.background.width =
			this.width +
			SWIMLANE_LABEL_SETTINGS.BACKGROUND_LEFT_PADDING +
			SWIMLANE_LABEL_SETTINGS.BACKGROUND_RIGHT_PADDING;
		this.background.height =
			SWIMLANE_LABEL_STYLE.fontSize +
			SWIMLANE_LABEL_SETTINGS.BACKGROUND_TOP_PADDING +
			SWIMLANE_LABEL_SETTINGS.BACKGROUND_BOTTOM_PADDING;

		this.x = this.background.x + this.background.width / 2 - this.width / 2;
		this.y = this.stickyY + this.background.height / 2 - this.height / 2;
	}

	public getBackground(): FancySprite {
		return this.background;
	}
}
